import { z } from 'zod';

/**
 * «Что нового» — содержание релиза, которое продукт показывает при входе.
 *
 * Артефакт лежит в `docs/releases/whats-new/<version>.json`, собирается
 * `/release` из той же курированной записи публичного changelog и попадает в
 * бандл UI на сборке. Схема здесь, а не рядом с файлами: по ней проверяет гейт
 * `scripts/check-whats-new.mjs` и читает UI, и обе стороны должны понимать
 * формат одинаково.
 *
 * Показанный текст исправить нельзя — он уже в глазах клиента, — поэтому
 * схема требовательна там, где ошибка не видна автору: локаль, которую забыли,
 * и ссылка на возможность, которой нет в каталоге.
 */

/** Локали, на которых говорит продукт. Пустая строка — не перевод. */
export const WhatsNewCopySchema = z.object({
  title: z.string().min(1),
  text: z.string().min(1),
});

export const WhatsNewLocalizedSchema = z.object({
  en: WhatsNewCopySchema,
  ru: WhatsNewCopySchema,
  uk: WhatsNewCopySchema,
  es: WhatsNewCopySchema,
});

export const WhatsNewItemSchema = WhatsNewLocalizedSchema.extend({
  /**
   * Идентификатор возможности из `packages/core/src/product-features.ts`.
   *
   * Нужен, чтобы пункт о возможности, недоступной на тарифе пользователя, был
   * ПОМЕЧЕН («доступно на Pro и выше»), а не скрыт: человек должен узнать о
   * том, чего у него нет. Тариф читается из каталога, а не набирается руками в
   * тексте, иначе пометка переживёт следующее изменение тарифной матрицы.
   */
  feature: z.string().min(1).optional(),
  /** Маршрут продукта, который открывает пункт. Внешние ссылки здесь не живут. */
  href: z.string().startsWith('/').optional(),
});

export const WhatsNewReleaseSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /** Дата выката, ISO. Отделяет пользователя, пришедшего ПОСЛЕ релиза. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  items: z.array(WhatsNewItemSchema).min(1),
});

/**
 * Релиз, которому нечего показать клиенту (инфраструктура, тесты, рефакторинг).
 *
 * Причина обязательна и должна быть написана человеком: молчание — не ответ
 * гейту, потому что «забыли» и «показывать нечего» выглядят одинаково.
 */
export const WhatsNewSkipSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  skip: z.string().min(10),
});

export const WhatsNewSchema = z.union([WhatsNewReleaseSchema, WhatsNewSkipSchema]);

export type WhatsNewCopy = z.infer<typeof WhatsNewCopySchema>;
export type WhatsNewItem = z.infer<typeof WhatsNewItemSchema>;
export type WhatsNewRelease = z.infer<typeof WhatsNewReleaseSchema>;
export type WhatsNew = z.infer<typeof WhatsNewSchema>;

/** Отличает показываемый релиз от пропущенного, не заставляя звать `'skip' in x`. */
export function isWhatsNewRelease(entry: WhatsNew): entry is WhatsNewRelease {
  return !('skip' in entry);
}

// ============================================================================
// Состояние «показано» — контракт между продуктом и сервером
// ============================================================================

/**
 * Последний релиз, поп-ап которого пользователь уже видел.
 *
 * Хранится на ПОЛЬЗОВАТЕЛЕ (`users.metadata.lastSeenRelease`), а не в браузере:
 * один и тот же человек заходит и в веб, и во встроенное приложение CRM, с
 * разных устройств, и увидеть релиз он должен один раз, а не один раз на
 * браузер.
 */
export const SeenReleaseRequestSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
});

export const SeenReleaseStateSchema = z.object({
  /** null — пользователь не видел ни одного поп-апа. */
  lastSeenRelease: z.string().nullable(),
  /**
   * Когда пользователь появился. Тот, кто пришёл ПОСЛЕ выката, историю не
   * получает: показывать ему новость о том, что было до него, незачем.
   */
  userCreatedAt: z.string().nullable(),
});

/**
 * Ответ `GET /api/v1/embedded/whats-new`.
 *
 * Встроенная оболочка не монтирует ни провайдер тарифа, ни провайдер флагов,
 * поэтому тариф приходит отсюда. `tier` — перечисление, а не строка из
 * колонки: `orgs.subscription_tier` это свободный TEXT, и незнакомое значение
 * («trial», пустая строка) сделало бы НЕДОСТУПНОЙ каждую возможность в
 * каталоге, то есть поп-ап пометил бы тарифом всё подряд.
 */
export const EmbeddedWhatsNewStateSchema = SeenReleaseStateSchema.extend({
  /** false — сессии некуда записать отметку: поп-ап не показывается. */
  canRecord: z.boolean(),
  // Перечень — здесь, а не импорт `subscriptionTierSchema` из `./stripe`: этот
  // файл грузит `scripts/check-whats-new.mjs` самим Node, без сборщика, и
  // относительный импорт без расширения там не разрешается — гейт падает с
  // ERR_MODULE_NOT_FOUND (так и упал #996). Расхождение с `stripe.ts` ловит
  // тест `test/schemas/whats-new-tier.test.ts`.
  tier: z.enum(['free', 'starter', 'pro', 'business', 'scale', 'enterprise']),
  /**
   * Может ли пользователь сессии открыть тарифы (`canSeeBilling` по его роли).
   * `.optional()` — фронт новее воркера не должен терять поп-ап целиком из-за
   * одного поля; без него кнопки нет, человек видит «спросите владельца».
   */
  canViewPlans: z.boolean().optional(),
});

/** Ответ на запись отметки — обеими поверхностями. */
export const SeenReleaseAckSchema = z.object({
  lastSeenRelease: z.string(),
});

export type SeenReleaseRequest = z.infer<typeof SeenReleaseRequestSchema>;
export type SeenReleaseState = z.infer<typeof SeenReleaseStateSchema>;
export type EmbeddedWhatsNewState = z.infer<typeof EmbeddedWhatsNewStateSchema>;
export type SeenReleaseAck = z.infer<typeof SeenReleaseAckSchema>;
